import update from 'immutability-helper';
import React from 'react'
import JSXModal from './Modal';
import WrappedTask from './WrappedTask';
import { modal as old_modal } from '../../core/utilities/modal'

const types = React.PropTypes;

// Helpers
/**
 * Filters object values based on a condition
 * @param {object} obj object to filter
 * @param {func} predicate function by which to filter
 * @returns {object[]} list of objects that match predicate
 */ 
function selectedFilter(obj, predicate) {
    let result = {}, key;
    for (key in obj) {
        if (obj.hasOwnProperty(key) && predicate(obj[key])) {
            result[key] = obj[key];
        }
    }
    return result;
};

/**
 * Gets the amount of key-value pairs in the object
 * @param {object} obj object to get the size of
 * @returns {int} total amount of key-value pairs
 */
function objectSize(obj) {
    var size = 0,
        key;
    for (key in obj) {
        if (obj.hasOwnProperty(key)) size++;
    }
    return size;
};

/**
 * Filters a props.children based on the provided function
 * @param {React.Children} children 
 * @param {React.Node => bool} func 
 */
function childrenFilter(children, func) {
    var childs = []
    React.Children.forEach(children, child => {
        if (func(child)){
            childs.push(child)
        }
    })
    return childs
}

/**
 * @component TaskTable - Table that displays selected tasks in Modal created 
 * by runSelectedTasks
 * @param {*} props 
 * @returns 
 */
function TaskTable(props) {
    return (
        <div className="panel panel-default">
            <style> {`
                .task-table-title {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: nowrap;
                }
                .task-table-collapse-toggle {
                    float: right;
                    margin: 0;
                }
            `} </style>
            <div className="panel-heading">
                <div className="task-table-title">
                    <span className="info-box-text panel-title">
                        {props.allDay ? (
                            "All Day Tasks"
                        ): (
                            "Tasks"
                        )}
                    </span>
                    {props.allDay && (
                        <button
                            data-toggle="collapse"
                            href="#all_day"
                            className="btn btn-secondary btn-fill btn-xs
                                task-table-collapse-toggle"
                        >
                            <span class="glyphicon glyphicon-menu-down"
                                aria-hidden="false"
                            />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={`panel-body ${props.allDay && "collapse"}`}
                id="all_day"
            >
                {React.Children.count(props.children) == 0 ? (
                    <p>No tasks found.</p>
                ) : (
                    <table className="" style={{ width: "100%" }}>
                        <colgroup>
                            <col />
                            <col className="task-bar" />
                            <col />
                            <col />
                        </colgroup>
                        <tbody>
                            {props.children}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

TaskTable.propTypes = {
    allDay: types.bool // Whether the task is displayed as all day
}

/**
 * @component Timeline component - represents the inner content in the 
 * UpcomingTasks component; including the all day TaskTable, the default 
 * TaskTable, and the associated drop-downs
 */
export default class Timeline extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedTasks: {},
            tasks: {},
            runningSingleKey: null,
            modal: {
                single: {
                    opened: false,
                    message: <div>error</div>,
                },
                multi: {
                    opened: false
                }
            }
        }
       this.generateTimelineChildren();
    }

    componentDidMount() {
        this._ismouncted = true;
    }

    componentWillUnmount() {
        this._ismouncted = false;
    }

    /**
     * In order to wrap each child of the timeline with our selected and run 
     * multiple props as well as keep track in Timeline so we know when to 
     * display the 'run selected' button, the timeline keeps a dictionary in 
     * its state (this.state.tasks), indexing by the tasks key for 
     * reconciliation.  - On render, the timeline will only render from the 
     * cloned children.  - On runSelectedTasks, Timeline will filter through all
     * those Elements selecting the tasks with `selected === true` to clone with
     * the childrenIndex prop so that ModalContent clones the props.children of
     * corresponding indices and renders them correctly in the JSXModal
    */
    generateTimelineChildren() {
        this.props.children.map((child, index) => { /* Create an obj to store 
            the task Element and selected status */
            this.state.tasks[index] = {
                selected: false, // Task has been selected for run multiple by the user
                runningSingle: false
            }
        });
    }

    selected  = {
        get(key) {
            if (this.state.tasks[key].selected == true) {
                return true
            }
            return false
        },
        getAll: () => Object.values( // Find all tasks that are selected in Timeline
            selectedFilter(this.state.tasks, task => task.selected === true)
        ),
        amount: () => {
            const selectedTasks = selectedFilter(this.state.tasks, task => task.selected === true)
            return objectSize(selectedTasks);
        },
        toggle: (key) => {
            this.setState((prevState) => {
                let newState = Object.assign(prevState, {})
                prevState.tasks[key].selected ? (
                    newState.tasks[key] = {
                        selected: !newState.tasks[key].selected
                    }
                ) : (
                    /**
                     * If the task was not selected and now is, we need to keep track of the index refering to the
                     * correct original task from this.props.children
                     */
                    newState.tasks[key] = {
                        selected: !newState.tasks[key].selected,
                        childrenIndex: key
                    }
                )
                return (newState);
            })
        },
    }

    /**
     * We need to keep track of which task to call runAction for
     * @param {*} key 
     */ 
    toggleRunSingle = (key) => {
        this.setState((prevState) => {
            let newState = Object.assign(prevState, {})
            prevState.tasks[key].selected && (
                newState.tasks[key] = {
                    selected: !newState.tasks[key].selected
                }
            )
            newState.runningSingleKey = key
            return newState
        }, (() => {
            this.props.children[key].props.confirm ? ( // If the task calls for confirmation
                this.modal.single.open(key, this.props.children[key].props.message) // Confirm
            ) : ( // Else
                this.runTask(this.props.children[key]) // Go ahead and just run the task
            )
        }).bind(this))
    }

    modal = { // The modal related callbacks
        single: {
            open : (key, message) => { // Open a modal for a single task
                this._ismouncted && this.setState(update(this.state, {
                        modal: {
                            single: {
                                opened: {$set: true},
                                message: {$set: message}
                            }
                        },
                        runningSingleKey: {$set: key}
                    }))
            },
            isOpen : () => { // Getter for the open state of the modal
                return this.state.modal.single.opened
            },
            cancel : (callback) => { // Function to be passed to the modal
                this._ismouncted && this.setState((prevState) => {
                    return(update(prevState, {
                        modal: {
                            single: {
                                opened: {$set: false},
                            }
                        }
                    }))
                })
            }
        },
        multi: {
            open : () => { // Open a modal for multiple tasks to run
                this._ismouncted && this.setState((prevState) => {
                    return(update(prevState, {
                        modal: {
                            multi: {
                                opened: {$set: true},
                            }
                        }
                    }))
                })
            },
            isOpen : () => { // Getter for the open state of the modal
                return this.state.modal.multi.opened
            },
            cancel : (callback) => { // Function to be passed to the modal
                this._ismouncted && this.setState((prevState) => {
                    return(update(prevState, {
                        modal: {
                            multi: {
                                opened: {$set: false},
                            }
                        }
                    }))
                })
            }
        }
    }

    resolveSingle = (resolve, success) => {
        this._ismounted && this.setState({
            runningSingleKey: success && (child == null) ? (null) : (undefined)
        }, resolve)
    }

    runTaskPromise(child) { // Promise for running the task
        var getChildParams;
        new Promise((resolve) => {
            if (child == null) {
                resolve(this.props.children[this.state.runningSingleKey])
            } else {
                resolve(child)
            }
        })
            .then((child) => {
                return new Promise (resolve => {
                    resolve([child, child.props.runAction])
                })
            }, () => {
                return new Promise (reject => {
                    reject(old_modal.showInfo("Task Failed: Couldn't find task data", "danger", "top", "center"))
                    throw 'Cannot access task.props.runAction';
                })
            })
            .then(([child, runActionFunction]) => {
                return new Promise (resolve => {
                    resolve([runActionFunction, child.props.message])
                })
            },
            () => {
                return new Promise (reject => {
                    reject(old_modal.showInfo("Task Failed: Couldn't find a solution for this task", "danger", "top", "center"))
                    throw 'Cannot access task.props.message'
                })
            })
            .then(([runActionFunction, taskMessage]) => {
                Promise.race([
                    new Promise((re) => {
                        setTimeout(re, 10000, ("timed out"))
                    }),
                    new Promise(res => {
                        runActionFunction(res)
                    })
                ])
                .then(result => {
                    if(result) {
                        return new Promise ((resolve) => {
                            old_modal.showInfo("Ran Task: " + taskMessage, "success", "top", "center")
                            setTimeout(() => {
                                this.modal.single.cancel()
                            }, 500)
                            resolve(true)
                        })
                    } else {
                        return new Promise ((resolve, reject) => {
                            reject(old_modal.showInfo("Task Failed: " + taskMessage, "danger", "top", "center"))
                        })
                    }
                })
                .then((success) => {
                    return new Promise((resolve) => {
                        this.resolveSingle(resolve, success)
                    }),
                    () => {
                        console.log('error')
                    }
                })
                .catch((error) => { // If debugging comment out!
                    console.log(error);
                    setTimeout(() => {
                        old_modal.showInfo("Unable to run one or more tasks", "danger", "top", "center")
                        this.modal.single.cancel()
                    }, 500)
                })
            })
    }

    runTasks = () => { // Run MULTIPLE selected tasks
        this.selected.getAll().map((taskObject) => {
            let child = this.props.children[taskObject.childrenIndex];
            this.runTaskPromise(child);
        })
        setTimeout(() => {
            old_modal.resetBackDrop()
        }, 1000)
        setTimeout(() => {
            old_modal.showInfo("Finished running tasks &check;", "info", "top", "center")
            this.modal.multi.cancel()
        }, 7000)
    }

    runTask = () => { 
        this.runTaskPromise(null)
    }

    render() {
        return (
            <div className="timeline"> 
                {this.selected.amount() > 1 && ( 
                    <div className="run-selected"> 
                        <btn
                            className="btn btn-fill btn-info btn-secondary pull-right" 
                            onClick={this.modal.multi.open}
                        >
                            Run Selected Tasks
                        </btn> 
                    </div> 
                )}
                <div className="panel-group">
                    <TaskTable openable allDay>
                        {childrenFilter(this.props.children, child => {
                            if (child.props.taskActionTime.includes('-')) {
                                return true
                            } else {
                                return false
                            }
                        }).map(
                            (child, index) => {
                                const newChild = React.createElement(WrappedTask, {
                                    selected: this.state.tasks[index].selected,
                                    toggleSelected: this.selected.toggle.bind(this, index),
                                    toggleRunSingle: this.toggleRunSingle.bind(this, index),
                                    openModal: this.modal.single.open.bind(this),
                                    expired: child.props.expired,
                                    viewOnly: child.props.viewOnly,
                                    donutColor: child.props.donutColor,
                                    taskActionTime: child.props.taskActionTime,
                                    key: `all-day-${index}`,
                                }, child)
                                return newChild
                            }
                        )} 
                    </TaskTable>
                    <TaskTable>
                        {childrenFilter(this.props.children, child => {
                            if (child.props.taskActionTime.includes('-')) {
                                return false
                            } else {
                                return true
                            }
                        }).map(
                            (child, index) => {
                                const newChild = React.createElement(WrappedTask, {
                                    selected: this.state.tasks[index].selected,
                                    toggleSelected: this.selected.toggle.bind(this, index),
                                    toggleRunSingle: this.toggleRunSingle.bind(this, index),
                                    openModal: this.modal.single.open.bind(this),
                                    expired: child.props.expired,
                                    viewOnly: child.props.viewOnly,
                                    donutColor: child.props.donutColor,
                                    taskActionTime: child.props.taskActionTime,
                                    confirm: child.props.confirm,
                                    key: `mid-day-${index}`,
                                }, child)
                                return newChild
                            }
                        )} 
                    </TaskTable>
                </div>
                <JSXModal 
                    isOpened={this.modal.multi.isOpen} 
                    cancel={this.modal.multi.cancel} 
                    title="Run Tasks" 
                    message="You are about to run these tasks" 
                    okText={"Run These Tasks"} 
                    okAction={this.runTasks.bind(this)}
                    size={{ height: "80", width: "70" }}
                >
                    {this.modal.multi.isOpen() && (
                        <TaskTable>
                            {this.selected.getAll().map((taskObject, key) => {
                                const index = taskObject.childrenIndex
                                return (
                                    taskObject.selected && (
                                        React.createElement(WrappedTask, {
                                            selected: true,
                                            renderMultiple: true,
                                            toggleSelected: this.selected.toggle.bind(this, index),
                                            ...this.props.children[key].props,
                                            key: key,
                                        }, this.props.children[key])
                                    )
                                );
                            })}
                        </TaskTable>
                    )}
                </JSXModal>
                <JSXModal
                    isOpened={this.modal.single.isOpen}
                    cancel={this.modal.single.cancel}
                    title="Run Task"
                    message="You are about to run this Task"
                    okText={"Run This Task"}
                    okAction={this.runTask.bind(this)}
                    size={{ height: "60", width: "30" }}
                >
                    {this.modal.multi.isOpen() && (
                        <p
                            className="task-text col text"
                            style={this.props.expired ? ({ color: "grey" }) : ({})}
                        >
                            {this.state.modal.single.runTaskMessage}
                        </p>
                    )}
                </JSXModal>

            </div> 
        ) 
    } 
}; 


// WEBPACK FOOTER //
// app/components/UpcomingTasks/Timeline.jsx